
import React, { useState, useEffect } from 'react';
import { Form, FormControl, FormField, FormItem } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { formatQRCode } from '@/utils/qrCodeUtils';

interface CodeFormValues {
  code: string;
}

interface CodeEntryFormProps {
  onCodeSubmit: (code: string) => void;
  isMobile: boolean;
}

const CodeEntryForm = ({ onCodeSubmit, isMobile }: CodeEntryFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Initialize form with react-hook-form
  const form = useForm<CodeFormValues>({
    defaultValues: {
      code: ''
    }
  });

  const handleSubmit = (values: CodeFormValues) => {
    if (!values.code.trim()) return;
    
    setIsSubmitting(true);
    
    // Process the code to ensure proper formatting
    let rawCode = values.code.trim().toUpperCase();
    
    // Add LHP- prefix if not already present
    const formattedCode = formatQRCode(rawCode);
    
    // Call the submission handler with properly formatted code
    onCodeSubmit(formattedCode);
    form.reset();
    
    // Add vibration feedback if supported on the device
    if (isMobile && 'vibrate' in navigator) {
      navigator.vibrate(200);
    }
    
    // Turn off submitting state after a delay for better UX
    setTimeout(() => {
      setIsSubmitting(false);
    }, 1500);
  };
  
  // Handle Enter key press for faster code entry
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && !isSubmitting && form.getValues('code').trim()) {
        form.handleSubmit(handleSubmit)();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [form, isSubmitting]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="flex gap-2">
        <FormField
          control={form.control}
          name="code"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormControl>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <span className="font-mono text-indigo-600 dark:text-indigo-400 font-bold">LHP-</span>
                  </div>
                  <Input 
                    {...field}
                    placeholder="Nhập mã xác nhận" 
                    disabled={isSubmitting}
                    className="pl-[52px] bg-white dark:bg-gray-800 border-2 border-indigo-300 dark:border-indigo-600 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/50"
                    autoCapitalize="characters"
                    autoComplete="off"
                    style={{ fontSize: isMobile ? '16px' : '14px', height: isMobile ? '44px' : '38px' }}
                    onChange={(e) => {
                      // Strip any LHP- prefix from manual input to avoid duplication
                      const value = e.target.value;
                      if (value.toUpperCase().startsWith('LHP-')) {
                        field.onChange(value.substring(4));
                      } else {
                        field.onChange(value);
                      }
                    }}
                  />
                </div>
              </FormControl>
            </FormItem>
          )}
        />
        <Button 
          type="submit" 
          disabled={isSubmitting || !form.watch('code').trim()}
          className={`shadow-md bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-medium ${isSubmitting ? "gap-2" : ""}`}
          size={isMobile ? "icon" : "default"}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              {!isMobile && "Đang xác nhận..."}
            </>
          ) : (
            <>
              {isMobile ? (
                <CheckCircle2 className="h-5 w-5" />
              ) : (
                "Xác nhận"
              )}
            </>
          )}
        </Button>
      </form>
    </Form>
  );
};

export default CodeEntryForm;
